import { Button, Card, Image, Col, Row, Select, Checkbox, Divider, Input, InputNumber } from 'antd'
import React, { useEffect, useState } from 'react'
import GradeService from '../../services/GradeService';
import QuestionService from '../../services/QuestionService';
import QuizService from '../../services/QuizService';
import SubjectService from '../../services/SubjectService';
import { openNotification } from '../../utils';
import { useNavigate } from 'react-router-dom';
import PackageService from '../../services/PackagesService';
import CourseService from '../../services/CourseService';

const QuizCreate = () => {
    const [packages, setPackages] = useState<any[]>([]);
    const [grades, setGrades] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>();
    const [selectedGrade, setSelectedGrade] = useState<number | null>(null);
    const [selectedLesson, setSelectedLesson] = useState<number | null>(null);
    const [selectedUnit, setSelectedUnit] = useState<number | null>(null);
    const [selectedSubject, setSelectedSubject] = useState<number | null>(null);
    const [pdfUrls, setPdfUrls] = useState<{ [key: number]: string }>({});
    const [selectedSubjectQuestions, setSelectedSubjectQuestions] = useState<any[]>([]);
    const { Option } = Select;

    const navigate = useNavigate()

    const [selectedQuestionIds, setSelectedQuestionIds] = useState<number[]>([]);
    const [title, setTitle] = useState<string>('');
    const [duration, setDuration] = useState<number>();
    const [selectedPackageIds, setSelectedPackageIds] = useState<number[]>([]);
    const [courseList, setCourseList] = useState<any>([]);
    const [selectedCourseList, setSelectedCourseList] = useState<any>([]);

    useEffect(() => {
        getGrades()
        getPackages()
        getCourseList();
    }, [])

    const getFile = (fileName: string, questionId: number) => {
        QuestionService.getFile(fileName).then((response: any) => {
            const imageBlob = new Blob([response.data], { type: 'image/png' }); // Adjust type if not png
            const url = URL.createObjectURL(imageBlob);
            setPdfUrls(prev => ({ ...prev, [questionId]: url }));
        }).catch((e: Error) => {
            console.error(e);
        });
    };

    const getGrades = () => {
        GradeService.getAllActive()
            .then((response: any) => {
                const data = response.data;
                setGrades(data);
                setLoading(false);
            })
            .catch((e: Error) => {
                console.log(e);
                setLoading(false);
            });
    };

    const handleSelectChange = (value: number[]) => {
        setSelectedPackageIds(value);
    };

    const handleSelectCourseList = (value: number[]) => {
        setSelectedCourseList(value);
    };

    const getCourseList = () => {
        CourseService.getAllActive()
            .then((response: any) => {
                const data = response.data;
                setCourseList(data);
            })
            .catch((e: Error) => {
                console.log(e);
            });
    };

    const getPackages = () => {
        PackageService.getAll()
            .then((response: any) => {
                const data = response.data;
                setPackages(data);
                setLoading(false);
            })
            .catch((e: Error) => {
                console.log(e);
                setLoading(false);
            });
    };

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setTitle(e.target.value);
    };

    const handleDurationChange = (value: number | null) => {
        setDuration(value ?? 0);
    };

    const createQuiz = () => {
        if (!title.trim() || selectedQuestionIds.length === 0) {
            openNotification('error', 'Hata', 'Lütfen sınav adını girin ve en az bir soru seçin.');
            return;
        }

        const postObj = {
            title: title,
            questionLessonPairs: selectedQuestionIds.map((q: any) => ({ questionId: q.questionId, lessonId: q.lessonId })),
            packageIds: selectedPackageIds,
            courseIds: selectedCourseList,
            duration: duration
        };

        setLoading(true);
        QuizService.create(postObj)
            .then((response: any) => {
                openNotification('success', 'Başarılı', 'Sınav Başarıyla Oluşturuldu.');
                setTitle('');
                setSelectedQuestionIds([]);
                navigate('/quiz');
                setLoading(false);
            })
            .catch((e: Error) => {
                console.error(e);
                setLoading(false);
            });
    };

    const handleGradeChange = (value: number) => {
        setSelectedGrade(value);
        setSelectedLesson(null); // Reset selected lesson when grade changes
        setSelectedUnit(null); // Reset selected unit when grade changes
        setSelectedSubject(null); // Reset selected subject when grade changes
        // No need to reset pdfUrls here
    };

    const handleLessonChange = (value: number) => {
        setSelectedLesson(value);
        setSelectedUnit(null); // Reset selected unit when lesson changes
        setSelectedSubject(null); // Reset selected subject when lesson changes
        // No need to reset pdfUrls here
    };

    const handleUnitChange = (value: number) => {
        setSelectedUnit(value);
        setSelectedSubject(null); // Reset selected subject when unit changes
        // No need to reset pdfUrls here
    };

    const handleSubjectChange = (value: number) => {
        setSelectedSubject(value);
        // No need to reset pdfUrls here
    };

    useEffect(() => {
        if (selectedSubject) {
            SubjectService.getQuestionsBySubjectId(selectedSubject)
                .then((response: any) => {
                const questions = response.data.filter((q:any) => q?.questionType === "QUIZ");
                    setSelectedSubjectQuestions(questions);
                    questions.forEach((question: any) => {
                        if (!pdfUrls[question.id]) {
                            getFile(question.question, question.id);
                        }
                    });
                })
                .catch((e: Error) => {
                    console.error(e);
                });
        } else {
            setSelectedSubjectQuestions([]);
        }
    }, [selectedSubject]);

    const selectedGradeLessons = grades.find((grade) => grade.id === selectedGrade)?.lessons || [];
    const selectedLessonUnits = (selectedGradeLessons.find((lesson: any) => lesson.id === selectedLesson)?.units || [])
        .filter((unit: any) => unit.gradeId === selectedGrade);
    const selectedUnitSubjects = selectedLessonUnits.find((unit: any) => unit.id === selectedUnit)?.subjects || [];

    const handleCheckboxChange = (questionId: number) => {
        setSelectedQuestionIds((prevSelectedIds: any) => {
            const currentQuestion = { questionId, lessonId: selectedLesson };
            if (prevSelectedIds.some((q: any) => q.questionId === questionId)) {
                return prevSelectedIds.filter((q: any) => q.questionId !== questionId);
            } else {
                return [...prevSelectedIds, currentQuestion];
            }
        });
    };

    return (
        <>
            <Card className='border-radius-md' loading={loading}>
                <Row justify={'space-between'} align={'middle'}>
                    <Col>
                        <h3 className='fs-24'>Sınav Oluştur</h3>
                    </Col>
                    <Col>
                        <Button className='btn btn-hover-dark bg-saha-purple font-color-white fw-700' onClick={createQuiz}>
                            Sınav Oluştur
                        </Button>
                    </Col>
                </Row>
                <Row gutter={24} className='mt-24'>
                    <Col xs={24} md={12} lg={8}>
                        <p className='fs-12 fw-700 m-0'>Sınav Adı</p>
                        <Input value={title} onChange={handleTitleChange} placeholder="Sınavın Adını Giriniz" />
                    </Col>
                    <Col xs={24} md={12} lg={8}>
                        <p className='fs-12 fw-700 m-0'>Sınav Süresi - dakika</p>
                        <InputNumber className='w-full' value={duration} onChange={handleDurationChange} placeholder="Sınav Süresi Giriniz" />
                    </Col>
                </Row>
                <Row gutter={24} className='mt-24'>
                    <Col span={24}>
                    <Select
                        mode="multiple"
                        className='w-full'
                        placeholder="Kurs Seçin"
                        onChange={handleSelectCourseList}
                        loading={loading}
                        value={selectedCourseList}
                    >
                        {courseList.map((pkg:any) => (
                            <Option key={pkg.id} value={pkg.id}>
                                {pkg.name}
                            </Option>
                        ))}
                    </Select>
                    </Col>
                </Row>
                {/* <Row gutter={24} className='mt-24'>
                    <Col span={24}>
                    <Select
                        mode="multiple"
                        className='w-full'
                        placeholder="Paket Seçin"
                        onChange={handleSelectChange}
                        loading={loading}
                        value={selectedPackageIds}
                    >
                        {packages.map((pkg) => (
                            <Option key={pkg.id} value={pkg.id}>
                                {pkg.name}
                            </Option>
                        ))}
                    </Select>
                    </Col>
                </Row> */}
                
            </Card>
            <Card className='mt-24 border-radius-md'>
                <h3 className='fs-20 mt-0'>
                    Soru Listesi
                </h3>
                <Row gutter={[24, 24]} className='mt-24'>
                    <Col xs={24} md={12} lg={12}>
                        <p className='fs-12 fw-700 m-0'>Sınıf</p>
                        <Select className='w-full' onChange={handleGradeChange}>
                            {grades.map((grade: any) => (
                                <Option key={grade.id} value={grade.id}>
                                    {grade.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} md={12} lg={12}>
                        <p className='fs-12 fw-700 m-0'>Ders</p>
                        <Select
                            className='w-full'
                            onChange={handleLessonChange}
                            value={selectedLesson}
                            disabled={!selectedGrade}
                        >
                            {selectedGradeLessons.map((lesson: any) => (
                                <Option key={lesson.id} value={lesson.id}>
                                    {lesson.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} md={12} lg={12}>
                        <p className='fs-12 fw-700 m-0'>Ünite</p>
                        <Select
                            className='w-full'
                            onChange={handleUnitChange}
                            value={selectedUnit}
                            disabled={!selectedLesson}
                        >
                            {selectedLessonUnits.map((unit: any) => (
                                <Option key={unit.id} value={unit.id}>
                                    {unit.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} md={12} lg={12}>
                        <p className='fs-12 fw-700 m-0'>Konu</p>
                        <Select className='w-full' onChange={handleSubjectChange} disabled={!selectedUnit} value={selectedSubject}>
                            {selectedUnitSubjects.map((subject: any) => (
                                <Option key={subject.id} value={subject.id}>
                                    {subject.title}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                </Row>
                <Divider></Divider>
                <Row>
                    {selectedSubjectQuestions.map((question: any, index: number) => (
                        <Col key={index} lg={8} md={12} xs={24}>
                            <Checkbox
                                className='saha-checkbox'
                                checked={selectedQuestionIds.some((q: any) => q.questionId === question.id)} // Güncellenmiş kontrol
                                onChange={() => handleCheckboxChange(question.id)}
                            >
                                Soru {index + 1}: {question.text}
                            </Checkbox>
                            {pdfUrls[question.id] && <Image src={pdfUrls[question.id]} width="100%" />}
                        </Col>
                    ))}
                </Row>
            </Card>
            <Card className='mt-24 border-radius-md'>
                <h3 className='fs-20 mt-0'>
                    Seçilmiş Sorular
                </h3>
                <Divider className='mt-0'></Divider>
                <Row>
                    {selectedQuestionIds.map((q: any, index: number) => {
                        const question = selectedSubjectQuestions.find((sq: any) => sq.id === q.questionId);
                        return (
                            <Col key={index} lg={8} md={12} xs={24}>
                                Soru {index + 1}: {question?.text}
                                {pdfUrls[q.questionId] && <Image src={pdfUrls[q.questionId]} width="100%" />}
                            </Col>
                        );
                    })}
                </Row>
            </Card>
        </>
    )
}

export default QuizCreate
